import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { API_URL, fetchWithAuth } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import type { Contact, ContactStage, ContactFilters } from '@/types/contacts';

export interface DashboardStats {
  totalContacts: number;
  newContactsThisMonth: number;
  totalDeals: number;
  totalRevenue: number;
  activeDeals: number;
  conversionRate: number;
  pendingTasks: number;
  overdueTasks: number;
}

export interface CRMState {
  contacts: Contact[];
  stats: DashboardStats | null;
  isLoading: boolean;
  error: string | null;
  filters: ContactFilters;
  version: number; // Incremented on every change to force re-renders
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface UseCRMReturn extends CRMState {
  // Actions
  refresh: () => Promise<void>;
  createContact: (data: Partial<Contact>) => Promise<Contact | null>;
  updateContact: (id: string, data: Partial<Contact>) => Promise<Contact | null>;
  deleteContact: (id: string) => Promise<boolean>;
  bulkDelete: (ids: string[]) => Promise<boolean>;
  updateStage: (id: string, stage: ContactStage) => Promise<boolean>;
  
  // Filters & Pagination
  setFilters: (filters: ContactFilters) => void;
  setSearchQuery: (query: string) => void;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  
  // Computed
  contactsByStage: Record<ContactStage, Contact[]>;
  filteredContacts: Contact[];
}

const initialStats: DashboardStats = {
  totalContacts: 0,
  newContactsThisMonth: 0,
  totalDeals: 0,
  totalRevenue: 0,
  activeDeals: 0,
  conversionRate: 0,
  pendingTasks: 0,
  overdueTasks: 0,
};

// Global cache to share data between component instances
const globalCache: {
  contacts: Contact[] | null;
  stats: DashboardStats | null;
  lastFetch: number;
} = {
  contacts: null,
  stats: null,
  lastFetch: 0,
};

const CACHE_DURATION = 30000; // 30 seconds

/**
 * Comprehensive CRM data hook with caching, optimistic updates, and error handling
 */
export function useCRM(): UseCRMReturn {
  const { token, isAuthenticated } = useAuth();
  const { toast } = useToast();
  
  const [contacts, setContacts] = useState<Contact[]>(globalCache.contacts || []);
  const [stats, setStats] = useState<DashboardStats | null>(globalCache.stats || null);
  const [isLoading, setIsLoading] = useState(false); // Never show loading if we have cache
  const [version, setVersion] = useState(0); // Force re-renders
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ContactFilters>({});
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 1,
  });
  
  // Update global cache when local state changes
  useEffect(() => {
    globalCache.contacts = contacts;
  }, [contacts]);
  
  useEffect(() => {
    globalCache.stats = stats;
  }, [stats]);

  // Build query string from filters and pagination
  const buildQueryString = useCallback(() => {
    const params = new URLSearchParams();
    params.set('page', pagination.page.toString());
    params.set('limit', pagination.limit.toString());
    
    if (filters.search) params.set('search', filters.search);
    if (filters.stages?.length) params.set('stages', filters.stages.join(','));
    if (filters.sources?.length) params.set('sources', filters.sources.join(','));
    if (filters.assignedTo?.length) params.set('assignedTo', filters.assignedTo.join(','));
    if (filters.tags?.length) params.set('tags', filters.tags.join(','));
    if (filters.dateRange?.from) params.set('from', filters.dateRange.from);
    if (filters.dateRange?.to) params.set('to', filters.dateRange.to);
    
    return params.toString();
  }, [filters, pagination.page, pagination.limit]);

  // Fetch contacts from API
  const fetchContacts = useCallback(async () => {
    if (!token || !isAuthenticated) return;
    
    // Only show loading if we don't have data yet
    if (contacts.length === 0) {
      setIsLoading(true);
    }
    setError(null);
    
    try {
      const queryString = buildQueryString();
      const response = await fetch(`${API_URL}/api/crm/contacts?${queryString}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please sign in again.');
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to load contacts: ${response.statusText}`);
      }

      const data = await response.json();
      
      setContacts(data.contacts || []);
      setPagination(prev => ({
        ...prev,
        total: data.total || 0,
        totalPages: data.totalPages || 1,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load contacts';
      setError(message);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: message,
      });
    } finally {
      setIsLoading(false);
    }
  }, [token, isAuthenticated, buildQueryString, toast, contacts.length]);

  // Fetch dashboard stats
  const fetchStats = useCallback(async () => {
    if (!token || !isAuthenticated) return;
    
    try {
      const response = await fetch(`${API_URL}/api/crm/contacts/stats/summary`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to load stats');
      }

      const data = await response.json();
      
      const totalContacts = data.total || 0;
      const byStage = data.byStage || {};
      const wonCount = byStage.closed_won?.count || 0;
      
      setStats({
        totalContacts,
        newContactsThisMonth: data.newThisMonth || 0,
        totalDeals: Object.values(byStage).reduce((sum: number, s: any) => sum + (s.count || 0), 0),
        totalRevenue: data.totalValue || 0,
        activeDeals: (byStage.proposal?.count || 0) + (byStage.negotiation?.count || 0),
        conversionRate: totalContacts > 0 ? Math.round((wonCount / totalContacts) * 100) : 0,
        pendingTasks: 0,
        overdueTasks: 0,
      });
    } catch (err) {
      console.error('Failed to fetch stats:', err);
      // Don't show toast for stats - non-critical
    }
  }, [token, isAuthenticated]);

  // Refresh all data
  const refresh = useCallback(async () => {
    await Promise.all([fetchContacts(), fetchStats()]);
  }, [fetchContacts, fetchStats]);

  // Create new contact
  const createContact = useCallback(async (contactData: Partial<Contact>): Promise<Contact | null> => {
    if (!token) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Not authenticated',
      });
      return null;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/crm/contacts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create contact');
      }

      const newContact = await response.json();
      
      // Optimistic update
      setContacts(prev => [newContact, ...prev]);
      setStats(prev => prev ? {
        ...prev,
        totalContacts: prev.totalContacts + 1,
      } : null);
      
      toast({
        title: 'Success',
        description: 'Contact created successfully',
      });
      
      return newContact;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create contact';
      toast({
        variant: 'destructive',
        title: 'Error',
        description: message,
      });
      throw err;
    }
  }, [token, toast]);

  // Update contact
  const updateContact = useCallback(async (id: string, contactData: Partial<Contact>): Promise<Contact | null> => {
    if (!token) return null;
    
    try {
      const response = await fetch(`${API_URL}/api/crm/contacts/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update contact');
      }

      const updated = await response.json();
      console.log('[useCRM] Update response:', updated);
      
      // Optimistic update
      setContacts(prev => {
        const newContacts = prev.map(c => c.id === id ? { ...c, ...updated } : c);
        console.log('[useCRM] Updated contacts:', newContacts.find(c => c.id === id));
        return newContacts;
      });
      
      // Force re-render of computed values
      setVersion(v => v + 1);
      
      toast({
        title: 'Success',
        description: 'Contact updated successfully',
      });
      
      return updated;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update contact';
      toast({
        variant: 'destructive',
        title: 'Error',
        description: message,
      });
      throw err;
    }
  }, [token, toast]);

  // Delete contact
  const deleteContact = useCallback(async (id: string): Promise<boolean> => {
    if (!token) return false;
    
    try {
      const response = await fetch(`${API_URL}/api/crm/contacts/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete contact');
      }

      // Optimistic update
      setContacts(prev => prev.filter(c => c.id !== id));
      setStats(prev => prev ? {
        ...prev,
        totalContacts: prev.totalContacts - 1,
      } : null);
      
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete contact';
      toast({
        variant: 'destructive',
        title: 'Error',
        description: message,
      });
      throw err;
    }
  }, [token, toast]);

  // Bulk delete contacts
  const bulkDelete = useCallback(async (ids: string[]): Promise<boolean> => {
    if (!token || ids.length === 0) return false;
    
    try {
      const response = await fetch(`${API_URL}/api/crm/contacts/bulk`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'delete', ids }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete contacts');
      }

      // Optimistic update
      setContacts(prev => prev.filter(c => !ids.includes(c.id)));
      setStats(prev => prev ? {
        ...prev,
        totalContacts: prev.totalContacts - ids.length,
      } : null);
      
      toast({
        title: 'Success',
        description: `${ids.length} contacts deleted`,
      });
      
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete contacts';
      toast({
        variant: 'destructive',
        title: 'Error',
        description: message,
      });
      throw err;
    }
  }, [token, toast]);

  // Update contact stage
  const updateStage = useCallback(async (id: string, stage: ContactStage): Promise<boolean> => {
    return updateContact(id, { stage }).then(() => true);
  }, [updateContact]);

  // Filter setters
  const setSearchQuery = useCallback((query: string) => {
    setFilters(prev => ({ ...prev, search: query }));
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  const setPage = useCallback((page: number) => {
    setPagination(prev => ({ ...prev, page }));
  }, []);

  const setLimit = useCallback((limit: number) => {
    setPagination(prev => ({ ...prev, limit, page: 1 }));
  }, []);

  // Computed: Group contacts by stage
  const contactsByStage = useMemo(() => {
    const grouped: Record<ContactStage, Contact[]> = {
      new: [],
      qualified: [],
      proposal: [],
      negotiation: [],
      closed_won: [],
      closed_lost: [],
    };
    contacts.forEach(contact => {
      grouped[contact.stage].push(contact);
    });
    return grouped;
  }, [contacts]);

  // Computed: Filter contacts locally (for search)
  const filteredContacts = useMemo(() => {
    if (!filters.search) return contacts;
    const query = filters.search.toLowerCase();
    return contacts.filter(
      contact =>
        contact.name.toLowerCase().includes(query) ||
        contact.email.toLowerCase().includes(query) ||
        contact.company?.toLowerCase().includes(query) ||
        contact.tags.some(tag => tag.toLowerCase().includes(query))
    );
  }, [contacts, filters.search]);

  // Track initial mount to prevent duplicate API calls
  const isInitialMount = useRef(true);
  const hasFetchedInitial = useRef(false);

  // Initial data fetch - only once when auth is ready
  useEffect(() => {
    if (token && isAuthenticated && !hasFetchedInitial.current) {
      hasFetchedInitial.current = true;
      // Use cache if available, otherwise fetch
      if (globalCache.contacts && globalCache.contacts.length > 0) {
        setContacts(globalCache.contacts);
        setStats(globalCache.stats);
        setIsLoading(false);
        // Only refresh if cache is stale
        if (Date.now() - globalCache.lastFetch > CACHE_DURATION) {
          refresh();
        }
      } else {
        refresh();
      }
    }
  }, [token, isAuthenticated, refresh]);

  // Refetch when filters or pagination changes (skip on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (token && isAuthenticated && hasFetchedInitial.current) {
      // Don't set loading true, just fetch in background
      fetchContacts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.page, pagination.limit]);

  return {
    contacts,
    stats,
    isLoading,
    error,
    filters,
    pagination,
    version,
    refresh,
    createContact,
    updateContact,
    deleteContact,
    bulkDelete,
    updateStage,
    setFilters,
    setSearchQuery,
    setPage,
    setLimit,
    contactsByStage,
    filteredContacts,
  };
}

export default useCRM;
